import { useEffect, useMemo } from 'react'
import { getGenerationFailure } from '@shared/errors'
import { TERMINAL_VIDEO_STATUSES, type VideoGeneration } from '@shared/index'
import type { PlaygroundJob, WorkflowWorkspaceResponse } from '@shared/types'
import { useQueries, useQueryClient, type UseQueryResult } from '@tanstack/react-query'

import { useAuth } from '@/lib/auth/AuthProvider'
import {
  forgetGeneration,
  generationKey,
  type GenerationOutcome,
  setGenerationAccount,
  settleGeneration,
  type TrackedGeneration,
  useGenerationTracking
} from '@/lib/generation-notifications'
import { api, type PlaygroundRunResponse } from '@/lib/queries/api'
import { playgroundKeys } from '@/lib/queries/usePlayground'
import { videoGenerationKeys } from '@/lib/queries/useVideoGenerations'
import { mergeWorkflowWorkspace, workflowKeys } from '@/lib/queries/useWorkflows'

type ObservedGeneration = PlaygroundRunResponse | WorkflowWorkspaceResponse | VideoGeneration | PlaygroundJob

/**
 * Keeps only what the settle effect reads. React Query structurally shares a stable `combine`
 * result, so the effect reruns when a result changes, not on every app render.
 */
const pickObservedResults = (results: Array<UseQueryResult<ObservedGeneration>>) =>
  results.map(({ data, error }) => ({ data, error }))

const observationKey = (entry: TrackedGeneration) =>
  entry.kind === 'workflow' ? `workflow:${entry.workflowId}` : generationKey(entry)

export function generationOutcome(entry: TrackedGeneration, data?: ObservedGeneration): GenerationOutcome {
  const pending = { complete: false, total: 0, ready: 0, failed: 0 }
  if (!data) return pending
  if (entry.kind === 'video') {
    const video = data as VideoGeneration
    return {
      complete: TERMINAL_VIDEO_STATUSES.includes(video.status),
      total: 1,
      ready: video.status === 'READY' ? 1 : 0,
      failed: video.status === 'FAILED' ? 1 : 0,
      canceled: video.status === 'CANCELED',
      failure: video.failure
    }
  }
  if (entry.kind === 'job') {
    const job = data as PlaygroundJob
    const failed = job.status === 'error' || job.status === 'moderation_failed'
    return {
      complete: job.status === 'done' || failed,
      total: 1,
      ready: job.status === 'done' ? 1 : 0,
      failed: failed ? 1 : 0,
      failure: failed ? getGenerationFailure(job) : undefined,
      warnings: job.generationWarnings?.map((warning) => warning.message)
    }
  }
  const workflow = entry.kind === 'workflow' ? (data as WorkflowWorkspaceResponse) : undefined
  const playground = entry.kind === 'playground' ? (data as PlaygroundRunResponse) : undefined
  const runs = workflow?.runs.filter((run) => entry.runIds?.includes(run.runId)) ?? (playground ? [playground.run] : [])
  // A temporarily incomplete GSI graph is still pending, not a completed empty batch.
  if (!runs.length || (entry.runIds && runs.length !== entry.runIds.length)) return pending
  const assets = workflow?.assets.filter((asset) => entry.runIds?.includes(asset.runId)) ?? playground?.assets ?? []
  const ready = runs.reduce((sum, run) => sum + run.completedAssetCount, 0)
  const failed = runs.reduce((sum, run) => sum + run.failedAssetCount, 0)
  const failure = assets.find((asset) => asset.status === 'error' || asset.status === 'moderation_failed')
  return {
    complete: runs.every((run) => ['done', 'partial', 'error'].includes(run.status)),
    total: ready + failed,
    ready,
    failed,
    failure: failure ? getGenerationFailure(failure) : failed ? getGenerationFailure({}) : undefined,
    warnings: assets.flatMap((asset) => asset.generationWarnings?.map((warning) => warning.message) ?? [])
  }
}

/** Keeps existing query-cache entries observed while the user visits other pages. */
export function useGenerationNotifications(navigate: (href: string) => void) {
  const { user } = useAuth()
  const client = useQueryClient()
  const state = useGenerationTracking()
  useEffect(() => setGenerationAccount(user?.userId ?? null), [user?.userId])
  const pending = useMemo(
    () => (user?.userId === state.accountId ? state.pending : []),
    [user?.userId, state.accountId, state.pending]
  )
  const observations = useMemo(
    () => [...new Map(pending.map((entry) => [observationKey(entry), entry])).values()],
    [pending]
  )
  const results = useQueries({
    queries: observations.map((entry) => ({
      queryKey:
        entry.kind === 'workflow'
          ? workflowKeys.workspace(entry.workflowId)
          : entry.kind === 'video'
            ? videoGenerationKeys.detail(entry.id)
            : entry.kind === 'job'
              ? playgroundKeys.status(entry.id)
              : playgroundKeys.run(entry.id, false),
      queryFn: async (): Promise<ObservedGeneration> => {
        if (entry.kind === 'workflow') {
          const workspace = await api.getWorkflow(entry.workflowId)
          return mergeWorkflowWorkspace(client.getQueryData(workflowKeys.workspace(entry.workflowId)), workspace)
        }
        if (entry.kind === 'video') return api.getVideoGeneration(entry.id)
        if (entry.kind === 'job') return api.getPlaygroundStatus(entry.id)
        return api.getPlaygroundRun(entry.id, false)
      },
      refetchInterval: 5000
    })),
    combine: pickObservedResults
  })

  useEffect(() => {
    const byKey = new Map(observations.map((entry, index) => [observationKey(entry), results[index]]))
    const completed = new Set<'workflow' | 'video' | 'playground'>()
    pending.forEach((entry) => {
      const result = byKey.get(observationKey(entry))
      if (!result) return
      if (result.error) {
        if ('status' in result.error && [401, 403, 404].includes(Number(result.error.status))) forgetGeneration(entry)
      } else if (result.data) {
        const outcome = generationOutcome(entry, result.data)
        settleGeneration(entry, outcome, navigate)
        if (outcome.complete) completed.add(entry.kind === 'job' ? 'playground' : entry.kind)
      }
    })
    for (const kind of completed) {
      const key =
        kind === 'workflow'
          ? workflowKeys.history()
          : kind === 'video'
            ? [...videoGenerationKeys.all, 'history']
            : playgroundKeys.historyFeed()
      void client.invalidateQueries({ queryKey: key })
    }
  }, [client, navigate, pending, observations, results])
}
